import { Cancel01Icon, File02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
  SortingState
} from "@tanstack/react-table";
import { instance } from "api/apiBase";
import { useEffect, useMemo, useState } from "react";
import {
  BaseInput,
  Button,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show,
  TruncateWithTooltip
} from "shared/components";
import { InputSize } from "shared/components/forms/BaseInput";
import { Pagination } from "shared/components/Pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";
import { getApplicantRole } from "utils/getApplicantRole";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { Table } from "shared/app/Table";
import type { BankStatements, EditTransactionsDrawerData } from "../../types";
import {
  checkIfSelectionsDiffer,
  columnHelper,
  filterAndSortTransactions,
  getAvailableData,
  getSharedColumns,
  isAssignedToOtherIncome,
  sortTransactions
} from "./utils";
import { toast } from "react-toastify";
import { Banner } from "shared/components/Banner";
import { getIncomeJobName, incomeTypeMap } from "../../utils";
import { CombinedIncome } from "../../types/income";
import { getErrorMessage } from "utils/error";

const ITEMS_PER_PAGE = Number(window.localStorage.getItem("per_page") || 10);

type Step = "selection" | "confirmation";
type Props = Partial<EditTransactionsDrawerData> & {
  onClose: () => void;
  applicant: Applicant;
  verificationId: number;
  applicationId: number;
  income?: CombinedIncome;
  allBankStatements?: BankStatements;
  onSuccess?: () => void;
};

const INIT_SORTING = { id: "amount", desc: true };

export const EditTransactions = ({
  onClose,
  applicant,
  verificationId,
  applicationId,
  allBankStatements,
  onSuccess,
  id: incomeId,
  additional_data,
  type
}: Props) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("selection");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [confirmationPage, setConfirmationPage] = useState(1);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [initialSelection, setInitialSelection] = useState<RowSelectionState>(
    {}
  );
  const [sortingState, setSortingState] = useState<SortingState>([
    INIT_SORTING
  ]);

  const isConfirmation = step === "confirmation";

  const { filteredBankStatements, transactions } = useMemo(
    () => getAvailableData(allBankStatements),
    [allBankStatements]
  );

  useEffect(() => {
    if (transactions.length) {
      const selection: RowSelectionState = {};
      transactions.forEach((transaction) => {
        if (
          transaction.income_id === incomeId ||
          (transaction.income_id && transaction.income_id !== incomeId)
        )
          selection[String(transaction.id)] = true;
      });

      setRowSelection(selection);
      setInitialSelection(selection);
    }
  }, []);

  const sortedTransactions = useMemo(
    () =>
      filterAndSortTransactions({
        transactions,
        search,
        sortingState,
        rowSelection,
        incomeId
      }),
    [transactions, search, sortingState, rowSelection, incomeId]
  );

  const selectionTotalPages = Math.ceil(
    sortedTransactions.length / ITEMS_PER_PAGE
  );
  const paginatedTransactions = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return sortedTransactions.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedTransactions, page]);

  const hasSelectionChanged = useMemo(
    () =>
      checkIfSelectionsDiffer(
        transactions,
        initialSelection,
        rowSelection,
        incomeId
      ),
    [transactions, initialSelection, rowSelection, incomeId]
  );

  const selectedTransactions = useMemo(() => {
    const selected = transactions.filter(
      (tx) =>
        rowSelection[String(tx.id)] && !isAssignedToOtherIncome(tx, incomeId)
    );
    return sortTransactions(selected, [INIT_SORTING], rowSelection, incomeId);
  }, [transactions, rowSelection, incomeId]);

  const confirmationTotalPages = Math.ceil(
    selectedTransactions.length / ITEMS_PER_PAGE
  );
  const paginatedConfirmationTransactions = useMemo(() => {
    const start = (confirmationPage - 1) * ITEMS_PER_PAGE;
    return selectedTransactions.slice(start, start + ITEMS_PER_PAGE);
  }, [selectedTransactions, confirmationPage]);

  const selectionColumns = useMemo(
    () => [
      columnHelper.display({
        id: "fixed_width checkbox custom_",
        size: 48,
        cell: ({ row }) => {
          const isDisabled = isAssignedToOtherIncome(row.original, incomeId);

          const checkbox = (
            <Checkbox
              size={CheckboxSize.SM}
              checked={row.getIsSelected()}
              disabled={isDisabled}
              onChange={row.getToggleSelectedHandler()}
              className={cx(isDisabled && "opacity-50")}
            />
          );

          if (!isDisabled) return <div className="px-4">{checkbox}</div>;

          return (
            <div className="px-4">
              <Tooltip placement="top" delay={{ open: 300 }}>
                <TooltipTrigger asChild>
                  <span>{checkbox}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px] rounded-lg border bg-alpha-800 px-3 py-2 text-xs text-white z-[60]">
                  This transaction was used to verify another income source
                </TooltipContent>
              </Tooltip>
            </div>
          );
        }
      }),
      ...getSharedColumns((row) => isAssignedToOtherIncome(row, incomeId))
    ],
    [incomeId, isAssignedToOtherIncome]
  );

  const confirmationColumns = useMemo(() => getSharedColumns(() => false), []);

  const selectionTable = useReactTable({
    data: paginatedTransactions,
    columns: selectionColumns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) =>
      !isAssignedToOtherIncome(row.original, incomeId),
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    getRowId: (row) => String(row.id)
  });

  const confirmationTable = useReactTable({
    data: paginatedConfirmationTransactions,
    columns: confirmationColumns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id)
  });

  const { mutate, isPending } = useMutation({
    mutationKey: [
      "EDIT_TRANSACTIONS",
      applicationId,
      verificationId,
      applicant.id,
      incomeId
    ],
    mutationFn: (itemIds: number[]) => {
      return instance
        .post(
          `/portal/v1/applications/${applicationId}/verifications/${verificationId}/income/${incomeId}/assign_items`,
          { applicant_id: applicant.id, item_ids: itemIds }
        )
        .then(({ data }) => data);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      toast.success("Income was successfully updated");
      onSuccess?.();
      onClose();
    }
  });

  const handleSave = () => {
    mutate(selectedTransactions.map((tx) => tx.id));
  };

  return (
    <div className="h-screen flex flex-col">
      <header className="px-6 py-5 text-lg font-bold flex justify-between items-center border-b border-alpha-100 bg-white">
        Edit verified income transactions
        <IconButton
          HugeIcon={Cancel01Icon}
          size={IconButtonSize.LG}
          variant={IconButtonVariant.TEXT_BASE}
          onClick={onClose}
        />
      </header>
      <Banner
        variant="warning"
        textTheme="base"
        borders="bottom"
        body="Editing income transactions may affect all summary metrics and decision
        recommendations."
        className="max-w-[540px]"
        HugeIcon={
          <HugeiconsIcon
            icon={InformationCircleIcon}
            className="shrink-0 text-warning-500"
            size="20"
          />
        }
      />
      <div className="px-6 py-5 border-b border-alpha-100 flex items-center justify-between bg-white">
        <div className="space-y-1">
          <h2 className="text-base font-medium text-content-1000">
            {getFullName({
              firstName: applicant.first_name,
              lastName: applicant.last_name,
              middleName: applicant.middle_name,
              short: true
            })}
          </h2>
          <p className="text-sm text-content-400 font-medium">
            {getApplicantRole(applicant.role)}
          </p>
        </div>

        <div className="space-y-1 text-right">
          <h2 className="text-base font-medium text-content-1000">
            {getIncomeJobName(additional_data)}
          </h2>
          <p className="text-sm text-content-400 font-medium">
            {type && incomeTypeMap[type]}
          </p>
        </div>
      </div>
      <Show if={filteredBankStatements.length}>
        <div className="px-6 py-5 space-y-4 border-b border-alpha-100 bg-white">
          <h2 className="text-base font-bold text-content-1000">
            Uploaded bank statements
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {filteredBankStatements.map((bankStatement, idx) => (
              <div
                className="flex items-center gap-2 min-w-0"
                key={`bank-statement-${bankStatement.asset?.asset?.filename}-${idx}`}
              >
                <HugeiconsIcon
                  icon={File02Icon}
                  size={20}
                  className="text-content-100 shrink-0"
                />
                <TruncateWithTooltip
                  content={
                    bankStatement.asset?.asset?.filename || `Statement #${idx}`
                  }
                  tooltipClassName="z-[1000]"
                  className="text-sm font-medium text-content-1000"
                />
              </div>
            ))}
          </div>
        </div>
      </Show>
      <div className="px-6 py-5 space-y-3 bg-background-900 flex-grow overflow-y-auto">
        <div className="flex items-center justify-between">
          <p className="text-sm text-content-1000 font-medium">
            {isConfirmation ? "Selected deposits" : "Deposits"}
          </p>
          <Show if={!isConfirmation}>
            <BaseInput
              id="search"
              type="search"
              value={search}
              onChange={(val: string) => {
                setSearch(val);
                setPage(1);
              }}
              size={InputSize.SM}
              className="max-w-[50%] w-full"
              placeholder="Search deposit"
            />
          </Show>
        </div>

        {isConfirmation ? (
          <>
            <Table
              borderLess
              tableClassName=""
              rowHeight="48px"
              tdClassName="!py-1"
              table={confirmationTable}
              borderLessRow
              disableMobileListView
            />
            <Show if={confirmationTotalPages > 1}>
              <Pagination
                className="justify-end"
                total={confirmationTotalPages}
                current={confirmationPage}
                onChange={setConfirmationPage}
              />
            </Show>
          </>
        ) : (
          <>
            <Table
              tableClassName=""
              rowHeight="48px"
              tdClassName="!py-1"
              table={selectionTable}
              disableMobileListView
              sortableKeys={["amount"]}
              sorted={sortingState}
              setSorted={setSortingState}
              getRowClassName={(row) =>
                isAssignedToOtherIncome(row.original, incomeId)
                  ? "bg-background-900 opacity-60"
                  : ""
              }
            />
            <Show if={selectionTotalPages > 1}>
              <Pagination
                className="justify-end"
                total={selectionTotalPages}
                current={page}
                onChange={setPage}
              />
            </Show>
          </>
        )}
      </div>

      <footer className="flex gap-2 justify-end p-6 border-t border-alpha-100">
        {isConfirmation ? (
          <>
            <Button className="w-full" onClick={() => setStep("selection")}>
              Back
            </Button>
            <Button
              className="w-full"
              variant={ButtonVariant.PRIMARY}
              onClick={handleSave}
              loading={isPending}
            >
              Save changes
            </Button>
          </>
        ) : (
          <>
            <Button className="w-full" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="w-full"
              variant={ButtonVariant.PRIMARY}
              disabled={
                !hasSelectionChanged || !Object.entries(rowSelection).length
              }
              onClick={() => {
                setConfirmationPage(1);
                setStep("confirmation");
              }}
            >
              Continue
            </Button>
          </>
        )}
      </footer>
    </div>
  );
};
